import React, { useCallback } from 'react';
import { RangeFacet } from '@1po/1po-bff-fe-spec/generated/catalog/references/iam/response/GetIAMReferencesResponse';
import { Button } from 'antd-v5';
import { TFunction } from 'i18next';
import { DownOutlinedIcon } from 'assets/icons';
import { SFilterPanel, Wrapper } from 'components/Filter/Filter.styled';
import { SAntDropdown } from 'pages/TiresPage/TireSearch/TiresSearchbar/TiresSearchbar.styled';
import { Box, CenteredSpin, Flex, Icon, MarginBox, SectionCollapse, Text } from 'UI';
import RangeFilter from './FilterRangeFilter';
import FilterTextFacetOverlay from './FilterTextFacetOverlay';
import { UsePrice } from '../Filter.shared';
import {
  FILTER_POSITION,
  FILTER_PRICES,
  FilterType,
  RANGE_FILTER_TYPE,
  RangeFacetLocal,
  TEXT_FILTER_TYPE,
  TextFacetLocal,
} from '../Filter.types';

interface FilterFacetItemProps {
  facet: TextFacetLocal | RangeFacetLocal;
  facetLabel: string;
  loading: boolean;
  inline: boolean;
  defaultActiveKeys: string[];
  type?: FilterType;
  t: TFunction;
  isValidPosition: (label: string, t: TFunction) => boolean;
  getTextChecked: (id: string, item: string) => boolean;
  setTextFilter: (id: string, item: string) => void;
  filterValues?: [number, number];
  setRangeFilterImmediate: (id: string, range?: { min: number; max: number }) => void;
  setRangeFilterDebounced: (id: string, range?: { min: number; max: number }) => void;
  usePrice?: UsePrice;
  open?: boolean;
  setOpen?: (open: boolean) => void;
}

interface TextFacetContentProps {
  facet: TextFacetLocal;
  facetLabel: string;
  loading: boolean;
  inline: boolean;
  defaultActiveKeys: string[];
  type?: FilterType;
  t: TFunction;
  isValidPosition: (label: string, t: TFunction) => boolean;
  getTextChecked: (id: string, item: string) => boolean;
  setTextFilter: (id: string, item: string) => void;
  open: boolean;
  onVisibleChange: (visible: boolean) => void;
  getPopupContainer: (trigger: HTMLElement) => HTMLElement;
}

interface RangeFacetContentProps {
  facet: RangeFacetLocal;
  facetLabel: string;
  loading: boolean;
  inline: boolean;
  defaultActiveKeys: string[];
  filterValues: [number, number];
  setRangeFilterImmediate: (id: string, range?: { min: number; max: number }) => void;
  setRangeFilterDebounced: (id: string, range?: { min: number; max: number }) => void;
  usePrice?: UsePrice;
}

const spinnerOverlayStyle = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
} as const;

const TextFacetContent: React.FC<TextFacetContentProps> = ({
  facet,
  facetLabel,
  loading,
  inline,
  defaultActiveKeys,
  type,
  t,
  isValidPosition,
  getTextChecked,
  setTextFilter,
  open,
  onVisibleChange,
  getPopupContainer,
}) => {
  const textFacetOverlay = (
    <FilterTextFacetOverlay
      facet={facet}
      loading={loading}
      isPosition={facet.id === FILTER_POSITION}
      getTextChecked={getTextChecked}
      setTextFilter={setTextFilter}
      type={type}
      t={t}
      isValidPosition={isValidPosition}
    />
  );

  if (inline) {
    return (
      <MarginBox mt={15}>
        <SAntDropdown
          overlay={
            <Box>
              <div style={{ position: 'relative' }}>
                {textFacetOverlay}
                {loading ? (
                  <div style={spinnerOverlayStyle}>
                    <Box>
                      <CenteredSpin />
                    </Box>
                  </div>
                ) : null}
              </div>
            </Box>
          }
          getPopupContainer={getPopupContainer}
          trigger={['click']}
          visible={open}
          onVisibleChange={onVisibleChange}
          bordered
        >
          <Button>
            <MarginBox mr={12} />
            <Text type="lg" color="grey35">
              {facetLabel}
            </Text>
            <Flex direction={'row-reverse'}>
              <Icon IconComponent={DownOutlinedIcon} size={18} color={'black'} />
            </Flex>
            <MarginBox mx={5} />
          </Button>
        </SAntDropdown>
      </MarginBox>
    );
  }

  return (
    <SectionCollapse key={facet.id} position={'end'} noShadow defaultActiveKey={defaultActiveKeys} width={300}>
      <SFilterPanel
        header={
          <Flex align={'center'}>
            <Text type="base">{facetLabel}</Text>
          </Flex>
        }
        key={facet.id}
      >
        <div style={{ position: 'relative' }}>
          {textFacetOverlay}
          {loading ? (
            <div style={spinnerOverlayStyle}>
              <Box>
                <CenteredSpin />
              </Box>
            </div>
          ) : null}
        </div>
      </SFilterPanel>
    </SectionCollapse>
  );
};

const RangeFacetContent: React.FC<RangeFacetContentProps> = ({
  facet,
  facetLabel,
  loading,
  inline,
  defaultActiveKeys,
  filterValues,
  setRangeFilterImmediate,
  setRangeFilterDebounced,
  usePrice,
}) => {
  const { min = 0, max = 0, numberOfItems = 0 } = facet as RangeFacet;
  const [fMin, fMax] = filterValues;
  const isChanged = fMin !== min || fMax !== max;
  const isPriceFacet = facet.id === FILTER_PRICES;
  const hasInsufficientItems = numberOfItems === 0 || numberOfItems === 1;
  const isInactive = !facet.active;
  const isSingleValue = min === max;
  const isRangeInvalid = isInactive || isSingleValue;
  const isNonPriceInvalid = isRangeInvalid || hasInsufficientItems;
  const isDisabled = !isChanged && (isPriceFacet ? isRangeInvalid : isNonPriceInvalid);
  const isSliderDisabled = isPriceFacet ? false : isDisabled;

  const rangeFilter = (
    <RangeFilter
      min={min}
      max={max}
      filterValues={filterValues}
      facet={facet}
      isInputsDisabled={isDisabled}
      isSliderDisabled={isSliderDisabled}
      setRangeFilterImmediate={setRangeFilterImmediate}
      setRangeFilterDebounced={setRangeFilterDebounced}
      usePrice={usePrice}
      inline={inline}
    />
  );

  if (inline) {
    return (
      <Flex>
        <MarginBox mt={3} mr={5}>
          <Text type={'light_dimmer'}>{facetLabel}</Text>
        </MarginBox>
        {rangeFilter}
      </Flex>
    );
  }

  return (
    <SectionCollapse key={facet.id} position={'end'} noShadow defaultActiveKey={defaultActiveKeys} width={300}>
      <SFilterPanel header={<Text type="base">{facetLabel}</Text>} key={facet.id}>
        {loading ? (
          <Box>
            <CenteredSpin />
          </Box>
        ) : (
          <Box>{rangeFilter}</Box>
        )}
      </SFilterPanel>
    </SectionCollapse>
  );
};

const FilterFacetItem: React.FC<FilterFacetItemProps> = ({
  facet,
  facetLabel,
  loading,
  inline,
  defaultActiveKeys,
  type,
  t,
  isValidPosition,
  getTextChecked,
  setTextFilter,
  filterValues = [0, 0],
  setRangeFilterImmediate,
  setRangeFilterDebounced,
  usePrice,
  open = false,
  setOpen,
}) => {
  const handleGetPopupContainer = useCallback((trigger: HTMLElement) => trigger.parentElement ?? trigger, []);

  const handleVisibleChange = useCallback(
    (visible: boolean) => {
      setOpen?.(visible);
    },
    [setOpen],
  );

  if (facet.type === TEXT_FILTER_TYPE) {
    return (
      <Wrapper>
        <TextFacetContent
          facet={facet as TextFacetLocal}
          facetLabel={facetLabel}
          loading={loading}
          inline={inline}
          defaultActiveKeys={defaultActiveKeys}
          type={type}
          t={t}
          isValidPosition={isValidPosition}
          getTextChecked={getTextChecked}
          setTextFilter={setTextFilter}
          open={open}
          onVisibleChange={handleVisibleChange}
          getPopupContainer={handleGetPopupContainer}
        />
      </Wrapper>
    );
  }

  if (facet.type === RANGE_FILTER_TYPE) {
    return (
      <Wrapper>
        <RangeFacetContent
          facet={facet as RangeFacetLocal}
          facetLabel={facetLabel}
          loading={loading}
          inline={inline}
          defaultActiveKeys={defaultActiveKeys}
          filterValues={filterValues}
          setRangeFilterImmediate={setRangeFilterImmediate}
          setRangeFilterDebounced={setRangeFilterDebounced}
          usePrice={usePrice}
        />
      </Wrapper>
    );
  }

  return null;
};

export default FilterFacetItem;
